/**
 * Очередь заданий на отсмотр.
 *
 * План: docs/PEDAGOGY_CROSS_REVIEW_PLAN.md
 * Порядок выката: docs/PEDAGOGY_CROSS_REVIEW_ROLLOUT.md (волна B)
 *
 * Здесь СОЗНАТЕЛЬНО нет ни одного типа с полями `resultKind`, `pairGroupId`
 * или `itemId` в контурах сотрудника и руководителя. Это не забывчивость:
 * сервер их и не отдаёт — граница проходит по `RETURNS TABLE` серверных RPC
 * (миграция 20260828110000). Если такое поле однажды понадобится на клиенте,
 * значит что-то пошло не так на сервере, и чинить надо там.
 *
 * Пары и анализ живут в отдельном блоке в конце файла — он доступен только
 * супер-админу и вызывается только с одноимённого экрана.
 */

import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

// ============================================================================
// Контур сотрудника
// ============================================================================

export interface PedagogyReviewAssignment {
  assignmentKey: string
  status: 'open' | 'in_progress' | 'done' | 'skipped' | 'expired' | 'returned'
  studioInternalId: number
  studioNumber: string
  cityName: string
  studioAddress: string
  trainingDate: string
  trainingTime: string
  groupNameRaw: string | null
  directionNameRaw: string | null
  teacherNameRaw: string | null
  attendeesCount: number | null
  dueAt: string
  isOverdue: boolean
  openedAt: string | null
  completedAt: string | null
  readAt: string | null
  closedReason: string | null
  resolvedTeacherId: string | null
  resolvedTeacherName: string | null
  resolvedTeacherStatus: string | null
}

export interface PedagogyReviewWorkload {
  periodId: number
  weekNumberInPeriod: number
  weekStart: string
  weekEnd: string
  isEvaluator: boolean
  targetCount: number
  dispatchPaused: boolean
  doneCount: number
  openCount: number
  canRequestAssignment: boolean
  unavailableReason: string | null
}

export interface PedagogyReviewNextAssignmentResult {
  assignmentKey: string | null
  granted: boolean
  reason: string | null
}

export type PedagogyReviewSkipReason =
  | 'recording_problem'
  | 'no_access'
  | 'no_camera'
  | 'lesson_mismatch'
  | 'queue_selection_error'

/** Зеркало public.pedagogy_review_reason_pauses_studio: причина ставит на паузу всю студию. */
export function pedagogyReasonPausesStudio(code: PedagogyReviewSkipReason): boolean {
  return code === 'no_access' || code === 'no_camera'
}

export type PedagogyReviewTechnicalCaseStatus = 'open' | 'excluded'
export type PedagogyReviewTechnicalVerificationResult = 'confirmed' | 'changed' | 'not_confirmed'
export type PedagogyReviewTechnicalCaseAction =
  | 'return_to_queue'
  | 'exclude_lesson'
  | 'exclude_lesson_request_studio'
  | 'pause_studio'

export interface PedagogyReviewTechnicalCase {
  caseId: number
  status: PedagogyReviewTechnicalCaseStatus
  reasonCode: PedagogyReviewSkipReason
  reasonLabel: string
  effectiveReasonCode: PedagogyReviewSkipReason
  reportedDetails: string | null
  studioInternalId: number
  studioNumber: string
  cityName: string
  studioAddress: string
  trainingDate: string
  trainingTime: string
  groupNameRaw: string | null
  directionNameRaw: string | null
  teacherNameRaw: string | null
  reportCount: number
  firstReportedAt: string
  lastReportedAt: string
  lastReportedByName: string | null
  pauseUntil: string | null
  verificationResult: PedagogyReviewTechnicalVerificationResult | null
  decisionAction: PedagogyReviewTechnicalCaseAction | null
  decisionComment: string | null
  decidedAt: string | null
  decidedByName: string | null
  totalCount: number
}

export interface PedagogyAssignmentTeacherOption {
  teacherId: string
  fullName: string
  status: 'candidate' | 'training' | 'working'
  partnerNumbers: number[]
  citiesText: string
  matchedAliases: string[]
  hasOtherAliases: boolean
}

export interface PedagogyAssignmentReviewInput {
  assignmentKey: string
  directionId: string
  teacherId: string
  coachName: string
  generalComment?: string | null
  scores: Array<{ criterionCode: string; score: number | null; comment?: string | null }>
}

export interface CreatePedagogyAssignmentTeacherResult {
  teacherId: string
  fullName: string
  status: 'training' | 'working'
  created: boolean
}

// ============================================================================
// Контур руководителя
// ============================================================================

export interface PedagogyReviewEvaluator {
  evaluatorUserId: string
  evaluatorName: string | null
  tgUsername: string | null
  activeFrom: string
  activeTo: string | null
  isActive: boolean
  dispatchEnabled: boolean
  availabilityStatus: 'active' | 'paused' | 'vacation' | 'sick_leave'
  openItemsPolicy: 'let_finish' | 'return_all' | null
  removalReason: string | null
  addedByName: string | null
  removedByName: string | null
}

export interface PedagogyReviewDispatchOverviewRow {
  periodId: number
  weekNumberInPeriod: number
  weekStart: string
  weekEnd: string
  evaluatorUserId: string
  evaluatorName: string | null
  isActive: boolean
  dispatchEnabled: boolean
  availabilityStatus: 'active' | 'paused' | 'vacation' | 'sick_leave'
  availabilityNote: string | null
  targetCount: number
  bufferCount: number
  maxOpenCount: number
  stretchLimit: number
  allowSelfRequest: boolean
  dispatchPaused: boolean
  assignedCount: number
  doneCount: number
  openCount: number
  overdueCount: number
  skippedCount: number
  poolSize: number
}

export interface SetPedagogyReviewWorkloadPlanInput {
  evaluatorUserId: string
  periodId: number
  weekNumberInPeriod: number
  targetCount: number
  bufferCount: number
  maxOpenCount: number
  stretchLimit: number
  allowSelfRequest: boolean
  dispatchPaused: boolean
}

/** Фактический срез очереди: без плана и предсказаний. */
export interface PedagogyReviewQueueSnapshot {
  rangeStart: string
  rangeEnd: string
  completedCount: number
  plannedCount: number
  queueCompletedCount: number
  manualCompletedCount: number
  historicalDonePerEvaluatorWeek: number
  openStudiosCount: number
  availableStudiosCount: number
  redZoneStudiosCount: number
  noAccessStudiosCount: number
  coveredStudiosCount: number
  coveredTwoStudiosCount: number
  coveredThreeStudiosCount: number
  eligibleReviewsCount: number
  eligibleCitiesCount: number
}

export interface PedagogyReviewQueuePreviewItem {
  position: number
  studioInternalId: number
  studioNumber: string
  cityName: string | null
  itemId: number
  trainingDate: string
  trainingTime: string
  directionName: string | null
  teacherName: string | null
  score: number
  priorityComponentsTotal: number
  priorityReasons: Array<{ label: string; points: number }>
  lastReviewDate: string | null
}

export interface PedagogyReviewQueuePreview {
  calculatedAt: string
  items: PedagogyReviewQueuePreviewItem[]
}

export interface SetPedagogyReviewEvaluatorAvailabilityInput {
  evaluatorUserId: string
  availabilityStatus: 'active' | 'paused' | 'vacation' | 'sick_leave'
  activeFrom?: string | null
  note?: string | null
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const e = error as { message?: string; details?: string; hint?: string; code?: string }
    return [e.message, e.details, e.hint, e.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function nullableText(value: unknown): string | null {
  return value == null ? null : String(value)
}

function nullableNumber(value: unknown): number | null {
  return value == null ? null : Number(value)
}

function mapAssignment(row: Record<string, unknown>): PedagogyReviewAssignment {
  return {
    assignmentKey: String(row.assignment_key),
    status: String(row.status) as PedagogyReviewAssignment['status'],
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: String(row.studio_number ?? ''),
    cityName: String(row.city_name ?? ''),
    studioAddress: String(row.studio_address ?? ''),
    trainingDate: String(row.training_date),
    trainingTime: String(row.training_time),
    groupNameRaw: nullableText(row.group_name_raw),
    directionNameRaw: nullableText(row.direction_name_raw),
    teacherNameRaw: nullableText(row.teacher_name_raw),
    attendeesCount: nullableNumber(row.attendees_count),
    dueAt: String(row.due_at),
    isOverdue: Boolean(row.is_overdue),
    openedAt: nullableText(row.opened_at),
    completedAt: nullableText(row.completed_at),
    readAt: nullableText(row.read_at),
    closedReason: nullableText(row.closed_reason),
    resolvedTeacherId: nullableText(row.resolved_teacher_id),
    resolvedTeacherName: nullableText(row.resolved_teacher_name),
    resolvedTeacherStatus: nullableText(row.resolved_teacher_status),
  }
}

export async function listMyPedagogyReviewAssignments(
  includeClosed = false
): Promise<PedagogyReviewAssignment[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_my_pedagogy_review_assignments', { p_include_closed: includeClosed })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(mapAssignment)
}

export async function getMyPedagogyReviewAssignment(
  assignmentKey: string
): Promise<PedagogyReviewAssignment | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_my_pedagogy_review_assignment', { p_assignment_key: assignmentKey })
  )

  if (error) throw new Error(formatError(error))
  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  return row ? mapAssignment(row) : null
}

/**
 * Недельный план сотрудника. `null` — календарь не знает такой недели; это не
 * ошибка и не отсутствие прав, поэтому и не исключение.
 */
export async function getMyPedagogyReviewWorkload(): Promise<PedagogyReviewWorkload | null> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_my_pedagogy_review_workload_v2', { p_on_date: null })
  )

  if (error) throw new Error(formatError(error))
  const rows = (data ?? []) as Array<Record<string, unknown>>
  const row = rows[0]
  if (!row) return null

  return {
    periodId: Number(row.period_id),
    weekNumberInPeriod: Number(row.week_number_in_period),
    weekStart: String(row.week_start),
    weekEnd: String(row.week_end),
    isEvaluator: Boolean(row.is_evaluator),
    targetCount: Number(row.target_count ?? 0),
    dispatchPaused: Boolean(row.dispatch_paused),
    doneCount: Number(row.done_count ?? 0),
    openCount: Number(row.open_count ?? 0),
    canRequestAssignment: Boolean(row.can_request_assignment),
    unavailableReason: nullableText(row.unavailable_reason),
  }
}

export async function requestNextPedagogyReviewAssignment(): Promise<PedagogyReviewNextAssignmentResult> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('request_next_pedagogy_review_assignment')
  )

  if (error) throw new Error(formatError(error))
  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  return {
    assignmentKey: nullableText(row?.assignment_key),
    granted: Boolean(row?.granted),
    reason: nullableText(row?.reason),
  }
}

export async function markPedagogyReviewAssignmentRead(assignmentKey: string): Promise<boolean> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('mark_pedagogy_review_assignment_read', { p_assignment_key: assignmentKey })
  )

  if (error) throw new Error(formatError(error))
  return Boolean(data)
}

/**
 * Закрывает своё задание и атомарно передаёт его в техническую очередь.
 * `false` одинаково означает чужое, уже закрытое или отсутствующее задание.
 */
export async function reportPedagogyReviewTechnicalIssue(
  assignmentKey: string,
  reason: PedagogyReviewSkipReason,
  details?: string | null
): Promise<boolean> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('report_pedagogy_review_technical_issue', {
      p_assignment_key: assignmentKey,
      p_reason: reason,
      p_details: details?.trim() || null,
    })
  )

  if (error) throw new Error(formatError(error))
  return Boolean(data)
}

export async function listPedagogyReviewTechnicalCases(
  status: PedagogyReviewTechnicalCaseStatus,
  limit = 100,
  offset = 0
): Promise<PedagogyReviewTechnicalCase[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_review_technical_cases', {
      p_status: status,
      p_limit: limit,
      p_offset: offset,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    caseId: Number(row.case_id),
    status: String(row.case_status) as PedagogyReviewTechnicalCaseStatus,
    reasonCode: String(row.reason_code) as PedagogyReviewSkipReason,
    reasonLabel: String(row.reason_label ?? ''),
    effectiveReasonCode: String(row.effective_reason_code ?? row.reason_code) as PedagogyReviewSkipReason,
    reportedDetails: nullableText(row.reported_details),
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: String(row.studio_number ?? ''),
    cityName: String(row.city_name ?? ''),
    studioAddress: String(row.studio_address ?? ''),
    trainingDate: String(row.training_date),
    trainingTime: String(row.training_time),
    groupNameRaw: nullableText(row.group_name_raw),
    directionNameRaw: nullableText(row.direction_name_raw),
    teacherNameRaw: nullableText(row.teacher_name_raw),
    reportCount: Number(row.report_count ?? 1),
    firstReportedAt: String(row.first_reported_at),
    lastReportedAt: String(row.last_reported_at),
    lastReportedByName: nullableText(row.last_reported_by_name),
    pauseUntil: nullableText(row.pause_until),
    verificationResult: nullableText(row.verification_result) as PedagogyReviewTechnicalVerificationResult | null,
    decisionAction: nullableText(row.decision_action) as PedagogyReviewTechnicalCaseAction | null,
    decisionComment: nullableText(row.decision_comment),
    decidedAt: nullableText(row.decided_at),
    decidedByName: nullableText(row.decided_by_name),
    totalCount: Number(row.total_count ?? 0),
  }))
}

export async function resolvePedagogyReviewTechnicalCase(
  input: {
    caseId: number
    verificationResult: PedagogyReviewTechnicalVerificationResult
    verifiedReason: PedagogyReviewSkipReason | null
    action: PedagogyReviewTechnicalCaseAction
    comment?: string | null
    /** Тем же решением закрыть остальные открытые случаи студии. */
    applyToStudio?: boolean
  }
): Promise<number> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('resolve_pedagogy_review_technical_case', {
      p_case_id: input.caseId,
      p_verification_result: input.verificationResult,
      p_verified_reason: input.verifiedReason,
      p_action: input.action,
      p_comment: input.comment?.trim() || null,
      p_apply_to_studio: input.applyToStudio ?? false,
    })
  )

  if (error) throw new Error(formatError(error))
  // Число решённых случаев; 0 — случай уже обработан.
  return Number(data ?? 0)
}

export interface PedagogyReviewQueueErrorNotification {
  decisionId: number
  queueAction: PedagogyReviewTechnicalCaseAction
  studioNumber: string
  cityName: string | null
  trainingDate: string
  reportedDetails: string | null
  decidedByName: string | null
  decidedAt: string
}

/** Колокольчик супер-админов: подтверждённые ошибки выдачи, которые ещё никто не открыл. */
export async function getPedagogyReviewQueueErrorNotifications(limit = 5): Promise<{
  unreadCount: number
  items: PedagogyReviewQueueErrorNotification[]
}> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_review_queue_error_notifications', { p_limit: limit })
  )

  if (error) throw new Error(formatError(error))
  const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
  const items = Array.isArray(payload.items) ? payload.items as Record<string, unknown>[] : []
  return {
    unreadCount: Number(payload.unread_count ?? 0),
    items: items.map(row => ({
      decisionId: Number(row.decision_id),
      queueAction: String(row.queue_action) as PedagogyReviewTechnicalCaseAction,
      studioNumber: String(row.studio_number ?? ''),
      cityName: nullableText(row.city_name),
      trainingDate: String(row.training_date ?? ''),
      reportedDetails: nullableText(row.reported_details),
      decidedByName: nullableText(row.decided_by_name),
      decidedAt: String(row.decided_at ?? ''),
    })),
  }
}

export async function markPedagogyReviewQueueErrorRead(decisionId: number): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('mark_pedagogy_review_queue_error_read', { p_decision_id: decisionId })
  )

  if (error) throw new Error(formatError(error))
}

export async function listMyPedagogyAssignmentTeacherOptions(
  assignmentKey: string
): Promise<PedagogyAssignmentTeacherOption[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_my_pedagogy_assignment_teacher_options', {
      p_assignment_key: assignmentKey,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
    const rawStatus = String(row.teacher_status ?? '')
    const status: PedagogyAssignmentTeacherOption['status'] = rawStatus === 'candidate'
      ? 'candidate'
      : rawStatus === 'training'
        ? 'training'
        : 'working'
    return {
      teacherId: String(row.teacher_id),
      fullName: String(row.full_name ?? ''),
      status,
      partnerNumbers: [],
      citiesText: '',
      matchedAliases: Array.isArray(row.matched_aliases)
        ? row.matched_aliases.map(value => String(value)).filter(Boolean)
        : [],
      hasOtherAliases: Boolean(row.has_other_aliases),
    }
  })
}

export async function createTeacherForPedagogyReviewAssignment(
  assignmentKey: string,
  fullName: string
): Promise<CreatePedagogyAssignmentTeacherResult> {
  const normalizedName = fullName.trim().replace(/\s+/g, ' ')
  if (!normalizedName) throw new Error('Укажите ФИО педагога')

  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>)('create_teacher_for_pedagogy_review_assignment', {
      p_assignment_key: assignmentKey,
      p_full_name: normalizedName,
    })
  )
  if (error) throw new Error(formatError(error))

  const row = ((data ?? []) as Array<Record<string, unknown>>)[0]
  if (!row?.teacher_id) throw new Error('Сервер не вернул карточку педагога')
  return {
    teacherId: String(row.teacher_id),
    fullName: String(row.full_name ?? normalizedName),
    status: String(row.teacher_status) === 'training' ? 'training' : 'working',
    created: Boolean(row.created),
  }
}

/**
 * Тот же список педагогов при правке уже сохранённой строки. Отдельная функция
 * нужна потому, что у сохранённой строки на руках только непрозрачный
 * entry_key: по нему сервер сам находит студию и дату занятия — и одинаково для
 * обоих хранилищ результата.
 */
export async function listMyTrainerReviewEntryTeacherOptions(
  entryKey: string
): Promise<PedagogyAssignmentTeacherOption[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_my_trainer_review_entry_teacher_options', {
      p_entry_key: entryKey,
    })
  )
  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => {
    const rawStatus = String(row.teacher_status ?? '')
    const status: PedagogyAssignmentTeacherOption['status'] = rawStatus === 'candidate'
      ? 'candidate'
      : rawStatus === 'training'
        ? 'training'
        : 'working'
    return {
      teacherId: String(row.teacher_id),
      fullName: String(row.full_name ?? ''),
      status,
      partnerNumbers: [],
      citiesText: '',
      matchedAliases: [],
      hasOtherAliases: false,
    }
  })
}

/** Saves an assignment without ever receiving its hidden result kind. */
export async function savePedagogyReviewAssignment(
  input: PedagogyAssignmentReviewInput
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('save_pedagogy_review_assignment', {
      p_assignment_key: input.assignmentKey,
      p_direction_id: Number(input.directionId),
      p_teacher_id: input.teacherId,
      p_coach_name: input.coachName,
      p_general_comment: input.generalComment ?? null,
      p_scores: input.scores.map(score => ({
        criterion_code: score.criterionCode,
        score: score.score,
        comment: score.comment ?? null,
      })),
    })
  )
  if (error) throw new Error(formatError(error))
  return String(data)
}

// ============================================================================
// Руководитель
// ============================================================================

export async function listPedagogyReviewEvaluators(): Promise<PedagogyReviewEvaluator[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_review_evaluators', { p_on_date: null })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    evaluatorUserId: String(row.evaluator_user_id),
    evaluatorName: nullableText(row.evaluator_name),
    tgUsername: nullableText(row.tg_username),
    activeFrom: String(row.active_from),
    activeTo: nullableText(row.active_to),
    isActive: Boolean(row.is_active),
    dispatchEnabled: Boolean(row.dispatch_enabled),
    availabilityStatus: (nullableText(row.availability_status) as PedagogyReviewEvaluator['availabilityStatus']) ?? 'active',
    openItemsPolicy: (nullableText(row.open_items_policy) as PedagogyReviewEvaluator['openItemsPolicy']),
    removalReason: nullableText(row.removal_reason),
    addedByName: nullableText(row.added_by_name),
    removedByName: nullableText(row.removed_by_name),
  }))
}

export async function setPedagogyReviewEvaluatorAvailability(
  input: SetPedagogyReviewEvaluatorAvailabilityInput
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>)('set_pedagogy_review_evaluator_availability', {
      p_evaluator_user_id: input.evaluatorUserId,
      p_availability_status: input.availabilityStatus,
      p_active_from: input.activeFrom ?? null,
      p_note: input.note ?? null,
    })
  )

  if (error) throw new Error(formatError(error))
}

export async function getPedagogyReviewDispatchOverview(
  periodId: number | null,
  weekNumberInPeriod: number | null
): Promise<PedagogyReviewDispatchOverviewRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_review_dispatch_overview', {
      p_period_id: periodId,
      p_week_number_in_period: weekNumberInPeriod,
    })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    periodId: Number(row.period_id),
    weekNumberInPeriod: Number(row.week_number_in_period),
    weekStart: String(row.week_start),
    weekEnd: String(row.week_end),
    evaluatorUserId: String(row.evaluator_user_id),
    evaluatorName: nullableText(row.evaluator_name),
    isActive: Boolean(row.is_active),
    dispatchEnabled: Boolean(row.dispatch_enabled),
    availabilityStatus: (nullableText(row.availability_status) as PedagogyReviewDispatchOverviewRow['availabilityStatus']) ?? 'active',
    availabilityNote: nullableText(row.availability_note),
    targetCount: Number(row.target_count ?? 0),
    bufferCount: Number(row.buffer_count ?? 0),
    maxOpenCount: Number(row.max_open_count ?? 0),
    stretchLimit: Number(row.stretch_limit ?? 0),
    allowSelfRequest: Boolean(row.allow_self_request),
    dispatchPaused: Boolean(row.dispatch_paused),
    assignedCount: Number(row.assigned_count ?? 0),
    doneCount: Number(row.done_count ?? 0),
    openCount: Number(row.open_count ?? 0),
    overdueCount: Number(row.overdue_count ?? 0),
    skippedCount: Number(row.skipped_count ?? 0),
    poolSize: Number(row.pool_size ?? 0),
  }))
}

function jsonRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}

export async function getPedagogyReviewQueueSnapshot(
  periodId: number,
  weekNumberInPeriod: number | null
): Promise<PedagogyReviewQueueSnapshot> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase().rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>)('get_pedagogy_review_queue_snapshot', {
      p_period_id: periodId,
      p_week_number_in_period: weekNumberInPeriod,
    })
  )
  if (error) throw new Error(formatError(error))
  const payload = jsonRecord(data)
  return {
    rangeStart: String(payload.rangeStart ?? ''),
    rangeEnd: String(payload.rangeEnd ?? ''),
    completedCount: Number(payload.completedCount ?? 0),
    plannedCount: Number(payload.plannedCount ?? 0),
    queueCompletedCount: Number(payload.queueCompletedCount ?? 0),
    manualCompletedCount: Number(payload.manualCompletedCount ?? 0),
    historicalDonePerEvaluatorWeek: Number(payload.historicalDonePerEvaluatorWeek ?? 0),
    openStudiosCount: Number(payload.openStudiosCount ?? 0),
    availableStudiosCount: Number(payload.availableStudiosCount ?? 0),
    redZoneStudiosCount: Number(payload.redZoneStudiosCount ?? 0),
    noAccessStudiosCount: Number(payload.noAccessStudiosCount ?? 0),
    coveredStudiosCount: Number(payload.coveredStudiosCount ?? 0),
    coveredTwoStudiosCount: Number(payload.coveredTwoStudiosCount ?? 0),
    coveredThreeStudiosCount: Number(payload.coveredThreeStudiosCount ?? 0),
    eligibleReviewsCount: Number(payload.eligibleReviewsCount ?? 0),
    eligibleCitiesCount: Number(payload.eligibleCitiesCount ?? 0),
  }
}

export async function previewPedagogyReviewQueueNextStudios(limit = 20): Promise<PedagogyReviewQueuePreview> {
  const { data, error } = await withAuthRecovery(() =>
    (getSupabase().rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }>)('preview_pedagogy_review_queue_next_studios_v2', {
      p_limit: limit,
    })
  )
  if (error) throw new Error(formatError(error))
  const payload = jsonRecord(data)
  const rows = Array.isArray(payload.items) ? payload.items : []
  return {
    calculatedAt: String(payload.calculatedAt ?? ''),
    items: rows.map(value => {
      const row = jsonRecord(value)
      return {
        position: Number(row.position ?? 0),
        studioInternalId: Number(row.studioInternalId ?? 0),
        studioNumber: String(row.studioNumber ?? ''),
        cityName: nullableText(row.cityName),
        itemId: Number(row.itemId ?? 0),
        trainingDate: String(row.trainingDate ?? ''),
        trainingTime: String(row.trainingTime ?? ''),
        directionName: nullableText(row.directionName),
        teacherName: nullableText(row.teacherName),
        score: Number(row.score ?? 0),
        priorityComponentsTotal: Number(row.priorityComponentsTotal ?? 0),
        priorityReasons: Array.isArray(row.priorityReasons)
          ? row.priorityReasons.map(value => {
              const reason = jsonRecord(value)
              return { label: String(reason.label ?? ''), points: Number(reason.points ?? 0) }
            }).filter(reason => reason.label.length > 0)
          : [],
        lastReviewDate: nullableText(row.lastReviewDate),
      }
    }),
  }
}

export async function setPedagogyReviewWorkloadPlan(
  input: SetPedagogyReviewWorkloadPlanInput
): Promise<void> {
  const supabase = getSupabase()
  const { error } = await withAuthRecovery(() =>
    supabase.rpc('set_pedagogy_review_workload_plan', {
      p_evaluator_user_id: input.evaluatorUserId,
      p_period_id: input.periodId,
      p_week_number_in_period: input.weekNumberInPeriod,
      p_target_count: input.targetCount,
      p_buffer_count: input.bufferCount,
      p_max_open_count: input.maxOpenCount,
      p_stretch_limit: input.stretchLimit,
      p_allow_self_request: input.allowSelfRequest,
      p_dispatch_paused: input.dispatchPaused,
    })
  )

  if (error) throw new Error(formatError(error))
}

// ============================================================================
// Супер-админ: пары и анализ
//
// Всё ниже вызывается ТОЛЬКО с экрана выборки. Сервер и так проверяет
// `is_super_admin()` внутри каждой функции, но и на клиенте эти вызовы не
// должны попадать в общие экраны раздела: лишний вызов из чужого места —
// лишний путь в журнале посещений.
// ============================================================================

export interface PedagogyReviewPair {
  pairGroupId: string
  itemId: number
  studioInternalId: number
  cityName: string
  trainingDate: string
  trainingTime: string
  teacherNameRaw: string | null
  officialUserId: string
  officialName: string | null
  officialStatus: string
  officialPercent: number | null
  officialIsCurator: boolean
  controlUserId: string
  controlName: string | null
  controlStatus: string
  controlPercent: number | null
  controlIsCurator: boolean
  deltaPercent: number | null
  pairKind: 'curator_vs_independent' | 'independent_vs_independent'
  isComplete: boolean
}

export interface PedagogyReviewPairSummary {
  pairKind: 'curator_vs_independent' | 'independent_vs_independent'
  pairsTotal: number
  pairsComplete: number
  avgDelta: number | null
  stddevDelta: number | null
  medianDelta: number | null
}

export async function listPedagogyReviewPairs(periodId: number | null): Promise<PedagogyReviewPair[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_review_pairs', { p_period_id: periodId })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    pairGroupId: String(row.pair_group_id),
    itemId: Number(row.item_id),
    studioInternalId: Number(row.studio_internal_id),
    cityName: String(row.city_name ?? ''),
    trainingDate: String(row.training_date),
    trainingTime: String(row.training_time),
    teacherNameRaw: nullableText(row.teacher_name_raw),
    officialUserId: String(row.official_user_id),
    officialName: nullableText(row.official_name),
    officialStatus: String(row.official_status),
    officialPercent: nullableNumber(row.official_percent),
    officialIsCurator: Boolean(row.official_is_curator),
    controlUserId: String(row.control_user_id),
    controlName: nullableText(row.control_name),
    controlStatus: String(row.control_status),
    controlPercent: nullableNumber(row.control_percent),
    controlIsCurator: Boolean(row.control_is_curator),
    deltaPercent: nullableNumber(row.delta_percent),
    pairKind: String(row.pair_kind) as PedagogyReviewPair['pairKind'],
    isComplete: Boolean(row.is_complete),
  }))
}

export async function getPedagogyReviewPairSummary(
  periodId: number | null
): Promise<PedagogyReviewPairSummary[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_review_pair_summary', { p_period_id: periodId })
  )

  if (error) throw new Error(formatError(error))
  return ((data ?? []) as Array<Record<string, unknown>>).map(row => ({
    pairKind: String(row.pair_kind) as PedagogyReviewPairSummary['pairKind'],
    pairsTotal: Number(row.pairs_total ?? 0),
    pairsComplete: Number(row.pairs_complete ?? 0),
    avgDelta: nullableNumber(row.avg_delta),
    stddevDelta: nullableNumber(row.stddev_delta),
    medianDelta: nullableNumber(row.median_delta),
  }))
}
